import { useCallback } from 'react';

import { useQuery, useQueryClient } from '@tanstack/react-query';
import { format, parse } from 'date-fns';
import { useDispatch } from 'react-redux';

import { useUpdateDonorScheduleMutation as useUpdateDonorScheduleMutationGql } from '@/app/services/donorSchedules';
import {
  fertilizationsApi,
  useCreateCommDairyFertilizationListMutation as useCreateCommDairyFertilizationListMutationGql,
  useGetFertilizationListQuery as useGetFertilizationListQueryGql,
  useGetShipmentGroupMastersQuery as useGetShipmentGroupMastersQueryGql,
  useLazyGetCurrentFertPredictionTechQuery as useLazyGetCurrentFertPredictionTechQueryGql,
  useLazyGetFertilizationListQuery as useLazyGetFertilizationListQueryGql,
  useSignOffMutation as useSignOffMutationGql,
  useUpdateFertTransactionMutation as useUpdateFertTransactionMutationGql,
  useUpdateIncubatorAssignmentMutation as useUpdateIncubatorAssignmentMutationGql,
  useUpdateMatingLabelsDupesMutation as useUpdateMatingLabelsDupesMutationGql,
  useUpdateMatingLabelsMutation as useUpdateMatingLabelsMutationGql,
} from '@/app/services/fertilizations';
import {
  freshShipmentsApi,
  useCombineShipmentsMutation as useCombineShipmentsMutationGql,
  useGetEstimationHeaderValQuery as useGetEstimationHeaderValQueryGql,
  useGetFreshShipmentListQuery as useGetFreshShipmentListQueryGql,
  useGetFreshShipmentQCQuery as useGetFreshShipmentQCQueryGql,
  useGetShipGroupListQuery as useGetShipGroupListQueryGql,
  useSendCommDairyFertilizationEmailMutation as useSendCommDairyEmailMutationGql,
  useUndoShipmentMutation as useUndoShipmentMutationGql,
  useUpdateFreshShipmentTransactionMutation as useUpdateFreshShipmentTransactionMutationGql,
  useUpdateQcFreshShipmentMutation as useUpdateQcFreshShipmentMutationGql,
  useUpdateShipmentGroupTransactionMutation as useUpdateShipmentGroupTransactionMutationGql,
} from '@/app/services/freshShipments';
import { useFeatureFlag } from '@/hooks/useFeatureFlag';
import {
  estimationHeaderValOptions,
  fertilizationListOptions,
  fertPredictionTechQueryOptions,
  freshShipmentListOptions,
  freshShipmentQCOptions,
  shipGroupListOptions,
  shipmentEstimationOptions,
  shipmentGroupMastersOptions,
  useCombineShipmentsRestMutation,
  useCreateCommDairyExcelRestMutation,
  useMatingLabelsDupesRestMutation,
  useMatingLabelsRestMutation,
  useSendCommDairyEmailRestMutation,
  useSignOffRestMutation,
  useUndoShipmentRestMutation,
  useUpdateDSTransactionRestMutation,
  useUpdateFertTransactionRestMutation,
  useUpdateFreshShipmentTransactionRestMutation,
  useUpdateIncubatorAssignmentRestMutation,
  useUpdateQcFreshShipmentRestMutation,
  useUpdateShipmentGroupTransactionRestMutation,
} from '@/services/fresh-shipment';

import type { EquipmentItem } from './types';

const FLAG = '29181_fresh_shipment_graphql_to_rest';

export { FLAG as FRESH_SHIPMENT_REST_FLAG };

export function useFreshShipmentCacheInvalidation() {
  const queryClient = useQueryClient();
  const dispatch = useDispatch();

  return useCallback(
    (tags: string[]) => {
      dispatch(freshShipmentsApi.util.invalidateTags(tags as never[]));
      dispatch(fertilizationsApi.util.invalidateTags(tags as never[]));
      void queryClient.invalidateQueries({
        queryKey: ['fresh-shipment'],
      });
    },
    [dispatch, queryClient],
  );
}

export function useShipGroupListQuery(params: {
  fertilizationDate: string | null;
  fertilizationSiteId: string | null;
}) {
  const isRest = useFeatureFlag(FLAG);
  const enabled = !!params.fertilizationDate && !!params.fertilizationSiteId;

  const restQuery = useQuery({
    ...shipGroupListOptions({
      fertilizationDate: params.fertilizationDate!,
      fertilizationSiteId: params.fertilizationSiteId!,
    }),
    enabled: isRest && enabled,
  });

  const gqlQuery = useGetShipGroupListQueryGql(
    {
      fertilizationDate: params.fertilizationDate,
      fertilizationSiteId: params.fertilizationSiteId as string,
    },
    { skip: isRest || !enabled },
  );

  if (isRest) {
    return {
      data: restQuery.data,
      isLoading: restQuery.isFetching,
      refetch: () => {
        void restQuery.refetch();
      },
    };
  }
  return {
    data: gqlQuery.data,
    isLoading: gqlQuery.isLoading,
    refetch: () => {
      void gqlQuery.refetch();
    },
  };
}

export function useFreshShipmentListQuery(params: {
  fertilizationDate: string | null;
  fertilizationSiteId: string | null;
  fertilizationSiteName: string | null;
}) {
  const isRest = useFeatureFlag(FLAG);
  const enabled =
    !!params.fertilizationDate &&
    !!params.fertilizationSiteId &&
    !!params.fertilizationSiteName;

  const restQuery = useQuery({
    ...freshShipmentListOptions({
      fertilizationDate: params.fertilizationDate!,
      fertilizationSiteId: params.fertilizationSiteId!,
      fertilizationSiteName: params.fertilizationSiteName!,
    }),
    enabled: isRest && enabled,
  });

  const gqlQuery = useGetFreshShipmentListQueryGql(
    {
      fertilizationDate: params.fertilizationDate!,
      fertilizationSiteId: params.fertilizationSiteId!,
      fertilizationSiteName: params.fertilizationSiteName!,
    },
    { skip: isRest || !enabled },
  );

  if (isRest) {
    return {
      data: restQuery.data,
      isLoading: restQuery.isFetching,
      refetch: () => {
        void restQuery.refetch();
      },
    };
  }
  return {
    data: gqlQuery.data,
    isLoading: gqlQuery.isLoading,
    refetch: () => {
      void gqlQuery.refetch();
    },
  };
}

export function useFertilizationListQuery(params: {
  fertilizationDate: string | null;
  fertilizationSiteId: string | null;
  shipGroupId: string | null;
}) {
  const isRest = useFeatureFlag(FLAG);
  const enabled =
    !!params.fertilizationDate &&
    !!params.fertilizationSiteId &&
    !!params.shipGroupId;

  const restQuery = useQuery({
    ...fertilizationListOptions({
      fertilizationDate: params.fertilizationDate!,
      fertilizationSiteId: params.fertilizationSiteId!,
      shipGroupId: params.shipGroupId!,
    }),
    enabled: isRest && enabled,
  });

  const gqlQuery = useGetFertilizationListQueryGql(
    {
      fertilizationDate: params.fertilizationDate!,
      fertilizationSiteId: params.fertilizationSiteId!,
      shipGroupId: params.shipGroupId!,
    },
    { skip: isRest || !enabled },
  );

  if (isRest) {
    return {
      data: restQuery.data,
      isLoading: restQuery.isFetching,
      refetch: () => {
        void restQuery.refetch();
      },
    };
  }
  return {
    data: gqlQuery.data,
    isLoading: gqlQuery.isLoading,
    refetch: () => {
      void gqlQuery.refetch();
    },
  };
}

export function useLazyFertilizationList() {
  const queryClient = useQueryClient();
  const isRest = useFeatureFlag(FLAG);
  const [gqlTrigger] = useLazyGetFertilizationListQueryGql();

  return useCallback(
    async (params: {
      fertilizationDate: string;
      fertilizationSiteId: string;
      shipGroupId: string;
    }) => {
      if (isRest) {
        return queryClient.fetchQuery(fertilizationListOptions(params));
      }
      return gqlTrigger(params).unwrap();
    },
    [isRest, gqlTrigger, queryClient],
  );
}

export function useFreshShipmentQCQuery(params: {
  fertilizationDate: string | null;
  fertilizationSiteId: string | null;
  shipGroupIds: string[] | null;
  skip?: boolean;
}) {
  const isRest = useFeatureFlag(FLAG);
  const enabled =
    !params.skip &&
    !!params.fertilizationDate &&
    !!params.fertilizationSiteId &&
    !!params.shipGroupIds &&
    params.shipGroupIds.length > 0;

  const restQuery = useQuery({
    ...freshShipmentQCOptions({
      fertilizationDate: params.fertilizationDate!,
      fertilizationSiteId: params.fertilizationSiteId!,
      shipGroupIds: params.shipGroupIds!,
    }),
    enabled: isRest && enabled,
  });

  const gqlQuery = useGetFreshShipmentQCQueryGql(
    {
      shipGroupIds: params.shipGroupIds!,
      fertilizationDate: params.fertilizationDate!,
      fertilizationSiteId: params.fertilizationSiteId!,
    },
    { skip: isRest || !enabled },
  );

  if (isRest) {
    return {
      data: restQuery.data,
      isLoading: restQuery.isFetching,
      refetch: () => {
        void restQuery.refetch();
      },
    };
  }
  return {
    data: gqlQuery.data,
    isLoading: gqlQuery.isLoading,
    refetch: () => {
      void gqlQuery.refetch();
    },
  };
}

export function useEstimationHeaderValQuery(params: {
  shipGroupId: string | null;
}) {
  const isRest = useFeatureFlag(FLAG);
  const enabled = !!params.shipGroupId;

  const restQuery = useQuery({
    ...estimationHeaderValOptions(params.shipGroupId!),
    enabled: isRest && enabled,
  });

  const gqlQuery = useGetEstimationHeaderValQueryGql(
    { shipGroupId: params.shipGroupId! },
    { skip: isRest || !enabled },
  );

  if (isRest) {
    return {
      data: restQuery.data,
      isLoading: restQuery.isFetching,
    };
  }
  return {
    data: gqlQuery.data?.estimationHeaderVal,
    isLoading: gqlQuery.isLoading,
  };
}

export function useLazyFertPredictionTech() {
  const queryClient = useQueryClient();
  const isRest = useFeatureFlag(FLAG);
  const [gqlTrigger] = useLazyGetCurrentFertPredictionTechQueryGql();

  return useCallback(
    async (fertilizationId: string): Promise<EquipmentItem | null> => {
      if (isRest) {
        return await queryClient.fetchQuery(
          fertPredictionTechQueryOptions(fertilizationId),
        );
      }
      const { data } = await gqlTrigger({ fertilizationId });
      const tech = data?.currentFertPredictionTechId;
      return tech ? { id: tech.id, name: tech.name } : null;
    },
    [isRest, gqlTrigger, queryClient],
  );
}

export function useShipmentGroupMastersQuery(params: {
  fertilizationSiteId: string | null;
  shipGroupId: string | null;
}) {
  const isRest = useFeatureFlag(FLAG);
  const enabled = !!params.fertilizationSiteId && !!params.shipGroupId;

  const restQuery = useQuery({
    ...shipmentGroupMastersOptions({
      fertilizationSiteId: params.fertilizationSiteId!,
      shipGroupId: params.shipGroupId!,
    }),
    enabled: isRest && enabled,
  });

  const gqlQuery = useGetShipmentGroupMastersQueryGql(
    {
      fertilizationSiteId: params.fertilizationSiteId!,
      shipGroupId: params.shipGroupId!,
    },
    { skip: isRest || !enabled },
  );

  if (isRest) {
    return {
      data: restQuery.data
        ? { shipmentGroupMasters: restQuery.data }
        : undefined,
      isLoading: restQuery.isFetching,
    };
  }
  return {
    data: gqlQuery.data,
    isLoading: gqlQuery.isLoading,
  };
}

export function useUpdateQcFreshShipment() {
  const isRest = useFeatureFlag(FLAG);
  const restMutation = useUpdateQcFreshShipmentRestMutation();
  const [gqlMutate, { isLoading: gqlLoading }] =
    useUpdateQcFreshShipmentMutationGql();

  const mutateAsync = useCallback(
    async (request: {
      fertilizationDate: string;
      fertilizationSiteId: string;
      shipGroupId: string;
      qcEHMDate10ml: Date | null;
      qcEHMDate20ml: Date | null;
      qcEHMDate40ml: Date | null;
      qcEHMDate125ml: Date | null;
      qcEHMDate250ml: Date | null;
      qcShippingTubeExpirationDate: Date | null;
    }) => {
      if (isRest) {
        return restMutation.mutateAsync(request);
      }
      const response = await gqlMutate(request);
      return response.data?.updateQcFreshShipment;
    },
    [isRest, restMutation, gqlMutate],
  );

  return {
    mutateAsync,
    isPending: isRest ? restMutation.isPending : gqlLoading,
  };
}

export function useUpdateFertTransaction() {
  const isRest = useFeatureFlag(FLAG);
  const restMutation = useUpdateFertTransactionRestMutation();
  const [gqlMutate, { isLoading: gqlLoading }] =
    useUpdateFertTransactionMutationGql();

  const mutateAsync = useCallback(
    async (params: {
      id: string;
      field: string;
      value: string;
      updatePredictedComments?: boolean;
    }) => {
      if (isRest) {
        return restMutation.mutateAsync(params);
      }
      const response = await gqlMutate(params);
      return response.data?.updateFertTransaction;
    },
    [isRest, restMutation, gqlMutate],
  );

  return {
    mutateAsync,
    isPending: isRest ? restMutation.isPending : gqlLoading,
  };
}

export function useUpdateDonorScheduleTransaction() {
  const isRest = useFeatureFlag(FLAG);
  const restMutation = useUpdateDSTransactionRestMutation();
  const [gqlMutate, { isLoading: gqlLoading }] =
    useUpdateDonorScheduleMutationGql();

  const mutateAsync = useCallback(
    async (params: {
      id: string;
      field: string;
      value: string;
      updatePredictedComments?: boolean;
      isIvf?: boolean;
    }) => {
      if (isRest) {
        return restMutation.mutateAsync(params);
      }
      return gqlMutate(params);
    },
    [isRest, restMutation, gqlMutate],
  );

  return {
    mutateAsync,
    isPending: isRest ? restMutation.isPending : gqlLoading,
  };
}

export function useUpdateFreshShipmentTransaction() {
  const isRest = useFeatureFlag(FLAG);
  const restMutation = useUpdateFreshShipmentTransactionRestMutation();
  const [gqlMutate, { isLoading: gqlLoading }] =
    useUpdateFreshShipmentTransactionMutationGql();

  const mutateAsync = useCallback(
    async (params: { id: string; field: string; value: string }) => {
      if (isRest) {
        return restMutation.mutateAsync(params);
      }
      return gqlMutate(params);
    },
    [isRest, restMutation, gqlMutate],
  );

  return {
    mutateAsync,
    isPending: isRest ? restMutation.isPending : gqlLoading,
  };
}

export function useUpdateShipmentGroupTransaction() {
  const isRest = useFeatureFlag(FLAG);
  const restMutation = useUpdateShipmentGroupTransactionRestMutation();
  const [gqlMutate, { isLoading: gqlLoading }] =
    useUpdateShipmentGroupTransactionMutationGql();

  const mutateAsync = useCallback(
    async (params: { id: string; field: string; value: string }) => {
      if (isRest) {
        return restMutation.mutateAsync(params);
      }
      return gqlMutate(params);
    },
    [isRest, restMutation, gqlMutate],
  );

  return {
    mutateAsync,
    isPending: isRest ? restMutation.isPending : gqlLoading,
  };
}

export function useUpdateIncubatorAssignment() {
  const isRest = useFeatureFlag(FLAG);
  const restMutation = useUpdateIncubatorAssignmentRestMutation();
  const [gqlMutate, { isLoading: gqlLoading }] =
    useUpdateIncubatorAssignmentMutationGql();

  const mutateAsync = useCallback(
    async (params: { fertilizationId: string; value: string }) => {
      if (isRest) {
        return restMutation.mutateAsync({
          fertilizationId: params.fertilizationId,
          shipGroupMasterId: params.value,
        });
      }
      return gqlMutate(params);
    },
    [isRest, restMutation, gqlMutate],
  );

  return {
    mutateAsync,
    isPending: isRest ? restMutation.isPending : gqlLoading,
  };
}

export function useSignOff() {
  const isRest = useFeatureFlag(FLAG);
  const restMutation = useSignOffRestMutation();
  const [gqlMutate, { isLoading: gqlLoading }] = useSignOffMutationGql();

  const mutateAsync = useCallback(
    async (params: {
      username: string;
      fertilizationDate: string;
      fertilizationSiteId: string;
      shipGroupId: string;
    }) => {
      if (isRest) {
        return restMutation.mutateAsync({
          ...params,
          fertilizationDate: format(
            parse(params.fertilizationDate, 'MM-dd-yyyy', new Date()),
            'yyyy-MM-dd',
          ),
        });
      }
      const response = await gqlMutate(params);
      return response.data?.generateSignOffEmail;
    },
    [isRest, restMutation, gqlMutate],
  );

  return {
    mutateAsync,
    isPending: isRest ? restMutation.isPending : gqlLoading,
  };
}

export function useUndoShipment() {
  const isRest = useFeatureFlag(FLAG);
  const restMutation = useUndoShipmentRestMutation();
  const [gqlMutate, { isLoading: gqlLoading }] = useUndoShipmentMutationGql();

  const mutateAsync = useCallback(
    async (params: { freshShipmentId: string; fertSiteId: string }) => {
      if (isRest) {
        return restMutation.mutateAsync(params.freshShipmentId);
      }
      return gqlMutate(params);
    },
    [isRest, restMutation, gqlMutate],
  );

  return {
    mutateAsync,
    isPending: isRest ? restMutation.isPending : gqlLoading,
  };
}

export function useCombineShipments() {
  const isRest = useFeatureFlag(FLAG);
  const restMutation = useCombineShipmentsRestMutation();
  const [gqlMutate, { isLoading: gqlLoading }] =
    useCombineShipmentsMutationGql();

  const mutateAsync = useCallback(
    async (freshShipIdList: { shipmentId: string }[]) => {
      if (isRest) {
        return restMutation.mutateAsync({ shipmentIds: freshShipIdList });
      }
      return gqlMutate({ freshShipIdList });
    },
    [isRest, restMutation, gqlMutate],
  );

  return {
    mutateAsync,
    isPending: isRest ? restMutation.isPending : gqlLoading,
  };
}

export function useCreateCommDairyExcel() {
  const isRest = useFeatureFlag(FLAG);
  const restMutation = useCreateCommDairyExcelRestMutation();
  const [gqlMutate, { isLoading: gqlLoading }] =
    useCreateCommDairyFertilizationListMutationGql();

  const mutateAsync = useCallback(
    async (params: {
      fertilizationDate: string;
      fertilizationSiteId: string;
      shipGroupId: string;
      d5: boolean;
    }): Promise<Blob> => {
      if (isRest) {
        return restMutation.mutateAsync(params);
      }
      const result = await gqlMutate(params);
      const excelBytes = result.data?.createCommDairyFertilizationExcel;
      if (!excelBytes)
        throw new Error('No Excel data received from the server');
      return new Blob([new Uint8Array(excelBytes)], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      });
    },
    [isRest, restMutation, gqlMutate],
  );

  return {
    mutateAsync,
    isPending: isRest ? restMutation.isPending : gqlLoading,
  };
}

export function useSendCommDairyEmail() {
  const isRest = useFeatureFlag(FLAG);
  const restMutation = useSendCommDairyEmailRestMutation();
  const [gqlMutate, { isLoading: gqlLoading }] =
    useSendCommDairyEmailMutationGql();

  const mutateAsync = useCallback(
    async (params: {
      fertilizationDate: string;
      fertilizationSiteId: string;
      shipGroupId: string;
      email: string;
      username: string;
      d5: boolean;
    }) => {
      if (isRest) {
        return restMutation.mutateAsync(params);
      }
      return gqlMutate(params);
    },
    [isRest, restMutation, gqlMutate],
  );

  return {
    mutateAsync,
    isPending: isRest ? restMutation.isPending : gqlLoading,
  };
}

export function useMatingLabels() {
  const isRest = useFeatureFlag(FLAG);
  const restMutation = useMatingLabelsRestMutation();
  const [gqlMutate, { isLoading: gqlLoading }] =
    useUpdateMatingLabelsMutationGql();

  const mutateAsync = useCallback(
    async (params: {
      fertilizationDate: string;
      site: string;
      shipGroups: string;
    }): Promise<Blob> => {
      if (isRest) {
        return restMutation.mutateAsync(params);
      }
      const result = await gqlMutate(params);
      const excelBytes = result.data?.updateMatingLabelsXlsx;
      if (!excelBytes)
        throw new Error('No Excel data received from the server');
      return new Blob([new Uint8Array(excelBytes)], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      });
    },
    [isRest, restMutation, gqlMutate],
  );

  return {
    mutateAsync,
    isPending: isRest ? restMutation.isPending : gqlLoading,
  };
}

export function useMatingLabelsDupes() {
  const isRest = useFeatureFlag(FLAG);
  const restMutation = useMatingLabelsDupesRestMutation();
  const [gqlMutate, { isLoading: gqlLoading }] =
    useUpdateMatingLabelsDupesMutationGql();

  const mutateAsync = useCallback(
    async (params: {
      fertilizationDate: string;
      site: string;
      shipGroups: string;
    }): Promise<Blob> => {
      if (isRest) {
        return restMutation.mutateAsync(params);
      }
      const result = await gqlMutate(params);
      const excelBytes = result.data?.updateMatingLabelsDupesXlsx;
      if (!excelBytes)
        throw new Error('No Excel data received from the server');
      return new Blob([new Uint8Array(excelBytes)], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      });
    },
    [isRest, restMutation, gqlMutate],
  );

  return {
    mutateAsync,
    isPending: isRest ? restMutation.isPending : gqlLoading,
  };
}

export function useShipmentEstimationQuery(params: {
  freshShipmentId: string;
  enabled?: boolean;
}) {
  const query = useQuery({
    ...shipmentEstimationOptions(params.freshShipmentId),
    enabled: params.enabled !== false && !!params.freshShipmentId,
  });

  return {
    data: query.data ?? null,
    isLoading: query.isFetching,
  };
}
